import { useState } from "react";
import { authClient } from "@/lib/auth";
import { toast } from "sonner";
import { sfx } from "@/lib/sounds";
import { UserPlus, UserCheck, Clock } from "lucide-react";

/**
 * Generic follow button. Works for ANY user (creator or normal).
 * Uses the /api/follow/{id} endpoint. Supports follow-request pending state
 * when the target user has privacy.require_follow_approval=true.
 */
export default function FollowButton({ userId, initialFollowing, initialPending = false, onChange, size = "md", className = "" }) {
  const [following, setFollowing] = useState(!!initialFollowing);
  const [pending, setPending] = useState(!!initialPending);
  const [busy, setBusy] = useState(false);

  const toggle = async () => {
    if (!userId || busy) return;
    setBusy(true);
    try {
      if (following || pending) {
        // Unfollow or cancel — DELETE endpoint clears both
        const { data } = await authClient.delete(`/follow/${userId}`);
        setFollowing(false); setPending(false);
        onChange?.({ following: false, pending: false, followers: data.followers });
        sfx.no();
      } else {
        const { data } = await authClient.post(`/follow/${userId}`);
        if (data.pending) {
          setPending(true);
          onChange?.({ following: false, pending: true, followers: data.followers });
          toast.success("Follow request sent");
        } else {
          setFollowing(true);
          onChange?.({ following: true, pending: false, followers: data.followers });
          sfx.pling();
        }
      }
    } catch (e) {
      sfx.error();
      const msg = e?.response?.data?.detail || "Sign in to follow";
      toast.error(typeof msg === "string" ? msg : "Follow failed");
    }
    setBusy(false);
  };

  const px = size === "sm" ? "px-3 py-1.5 text-[10px]" : "px-5 py-2.5 text-xs";
  const label = pending ? "Requested" : (following ? "Following" : "Follow");
  const Icon = pending ? Clock : (following ? UserCheck : UserPlus);
  return (
    <button
      onClick={toggle}
      disabled={busy}
      data-testid={`follow-btn-${userId}`}
      className={`inline-flex items-center gap-2 rounded-full uppercase tracking-widest font-semibold transition-colors disabled:opacity-60 ${px} ${
        following ? "border border-white/20 hover:border-red-400/50 text-white/80"
        : pending ? "border border-yellow-500/30 text-yellow-300 hover:border-red-400/50"
        : "bg-[#FFBF00] hover:bg-[#FF8C00] text-black"
      } ${className}`}
    >
      <Icon size={size === "sm" ? 12 : 14} />
      {label}
    </button>
  );
}
